/**
 * doctor.ts — `zelari-code doctor` diagnostic.
 *
 * Run after a fresh install (or when something looks wrong) to check:
 *   1. Bin shim health (does `zelari-code.cmd` / `zelari-code` exist
 *      in the npm global prefix, and does it reference the right
 *      package install?).
 *   2. Node availability (`node` on PATH, version >= engines.node).
 *   3. CLI bundle presence (did `npm run build:cli` produce
 *      `dist/cli/main.bundled.js`?).
 *   4. Runtime deps (ink / react / zod / ink-text-input resolvable
 *      from the package root).
 *   5. @zelari/core dependency (only checked if it appears in the
 *      package's deps or devDeps — it's a build-time dep for the
 *      workspace, and a runtime dep only for the source/tsx fallback
 *      path in bin/zelari-code.js).
 *   6. PATH includes the npm global prefix.
 *
 * Output is human-readable, with one line per check + a trailing
 * summary. Non-zero exit code if any critical check fails so this
 * can be used in CI / install scripts.
 *
 * The function never throws: it catches all per-check errors and
 * reports them as "FAIL" with the error message. The final return
 * value tells the caller whether the install is healthy.
 *
 * @see scripts/postinstall.mjs — the install-time shim check
 * @see src/cli/updater.ts — the self-update mechanism
 */

import { execSync } from "node:child_process";
import { existsSync, readFileSync, readlinkSync, statSync } from "node:fs";
import { createRequire } from "node:module";
import { fileURLToPath } from "node:url";
import path from "node:path";
import {
  checkAgentNode,
  checkAgentGit,
  checkAgentBash,
  type PrereqResult,
} from "./prereqChecks.js";
import { getMetricsLogger, readMetrics } from "../metrics.js";
import {
  formatContextGrowthSummary,
  summarizeContextGrowth,
} from "./contextGrowthSummary.js";

const require = createRequire(import.meta.url);
const __dirname = path.dirname(fileURLToPath(import.meta.url));
// src/cli/utils/doctor.ts → <pkg>/src/cli/utils/doctor.ts
// We need <pkg> root. In dev (tsx) this is src/cli/utils/, in prod (bundled) it's dist/cli/utils/.
// In both cases we want the package root: that's `../../` from src/cli/utils, and `../../../` from dist/cli/utils/...
// Simplest: walk up until we find package.json with our name.
const packageRoot = findPackageRoot(__dirname);

function findPackageRoot(start: string): string {
  let dir = start;
  // Walk at most 6 levels up (src/cli/utils -> src/cli -> src -> root, dist/cli/utils -> dist/cli -> dist -> root).
  for (let i = 0; i < 6; i += 1) {
    const candidate = path.join(dir, "package.json");
    if (existsSync(candidate)) {
      try {
        const pkg = JSON.parse(readFileSync(candidate, "utf8")) as {
          name?: string;
        };
        if (pkg.name === "zelari-code") return dir;
      } catch {
        // ignore
      }
    }
    const parent = path.dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  // Fallback: assume standard layout.
  return path.resolve(__dirname, "..", "..", "..");
}

type CheckResult = {
  ok: boolean;
  message: string;
  /** 'critical' = blocks the install; 'warn' = non-blocking issue. */
  severity: "critical" | "warn";
};

const OK = (message: string): CheckResult => ({
  ok: true,
  message,
  severity: "critical",
});
const FAIL = (
  message: string,
  severity: "critical" | "warn" = "critical",
): CheckResult => ({
  ok: false,
  message,
  severity,
});
const WARN = (message: string): CheckResult => ({
  ok: false,
  message,
  severity: "warn",
});

/** Run a shell command and return trimmed stdout. Empty string on failure. */
function tryExec(cmd: string): string {
  try {
    return execSync(cmd, {
      encoding: "utf8",
      stdio: ["ignore", "pipe", "ignore"],
    }).trim();
  } catch {
    return "";
  }
}

/** Read the installed package.json (for name + version + bin path). */
function readPackageJson(): {
  name: string;
  version: string;
  bin?: string | Record<string, string>;
} | null {
  try {
    const pkgPath = path.join(packageRoot, "package.json");
    return JSON.parse(readFileSync(pkgPath, "utf8")) as {
      name: string;
      version: string;
      bin?: string | Record<string, string>;
    };
  } catch {
    return null;
  }
}

/**
 * npm global prefix. Prefer `npm prefix -g` over `npm_config_prefix`:
 * `npm run` (and `npm run … --prefix <pkg>`) sets that env to the *local*
 * package dir, so doctor looked for `<pkg>/zelari-code.cmd` and the
 * Desktop first-run gate went red during `npm run desktop:dev`.
 */
function getGlobalPrefix(): string {
  const fromNpm = tryExec("npm prefix -g");
  if (fromNpm) return fromNpm;
  return (
    process.env.npm_config_prefix ||
    process.env.NPM_CONFIG_PREFIX ||
    ""
  ).trim();
}

/** True when this process is a git/source checkout, not an `npm i -g` install. */
function isSourceCheckout(): boolean {
  return (
    existsSync(path.join(packageRoot, "src", "cli", "main.ts")) &&
    existsSync(path.join(packageRoot, "apps", "desktop", "package.json"))
  );
}

/** Check the bin shim in the global prefix. */
function checkShim(pkgName: string): CheckResult {
  const prefix = getGlobalPrefix();
  if (!prefix) {
    return WARN("npm global prefix not detectable (run inside an npm context)");
  }
  const isWin = process.platform === "win32";
  const shimName = isWin ? "zelari-code.cmd" : "zelari-code";
  const shimPath = path.join(prefix, shimName);
  if (!existsSync(shimPath)) {
    const localBin = path.join(packageRoot, "bin", "zelari-code.js");
    if (isSourceCheckout() && existsSync(localBin)) {
      // Desktop `npm run desktop:dev` runs this CLI via bin/zelari-code.js.
      // A missing global shim must not block the first-run gate.
      return WARN(
        `global shim not found at ${shimPath}\n` +
          `         source checkout — using ${localBin}\n` +
          `         optional: npm install -g ${pkgName}@latest --force`,
      );
    }
    return FAIL(
      `shim not found at ${shimPath}\n` +
        `         fix:  npm install -g ${pkgName}@latest --force`,
    );
  }
  try {
    const st = statSync(shimPath);
    if (isWin) {
      const content = readFileSync(shimPath, "utf8");
      if (
        content.includes(`${pkgName}\\bin\\`) ||
        content.includes(`${pkgName}/bin/`)
      ) {
        return OK(`shim OK at ${shimPath} (${st.size} bytes)`);
      }
      return FAIL(
        `shim at ${shimPath} does not reference ${pkgName}/bin/\n` +
          `         fix:  npm install -g ${pkgName}@latest --force`,
      );
    }
    let target: string;
    try {
      target = readlinkSync(shimPath);
    } catch {
      return FAIL(
        `shim at ${shimPath} is not a symlink (POSIX global installs should be symlinks)\n` +
          `         fix:  npm install -g ${pkgName}@latest --force`,
      );
    }
    const resolved = path.resolve(path.dirname(shimPath), target);
    const expected = path.join(
      prefix,
      "node_modules",
      pkgName,
      "bin",
      "zelari-code.js",
    );
    if (resolved === expected) {
      return OK(`shim OK at ${shimPath} → ${resolved}`);
    }
    return FAIL(
      `shim at ${shimPath} points to ${resolved}\n` +
        `         expected ${expected}\n` +
        `         fix:  npm install -g ${pkgName}@latest --force`,
    );
  } catch (err) {
    return FAIL(
      `could not inspect shim: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** Check that node is on PATH and meets the engines requirement. */
function checkNode(
  pkg: {
    name?: string;
    version?: string;
    engines?: { node?: string };
    bin?: string | Record<string, string>;
  } | null,
): CheckResult {
  const raw = tryExec("node --version");
  if (!raw) {
    return FAIL("`node` not found on PATH");
  }
  // raw is like "v20.11.1"
  const m = raw.match(/^v?(\d+)\.(\d+)\.(\d+)/);
  if (!m) {
    return WARN(`could not parse node version: ${raw}`);
  }
  const major = Number(m[1]);
  // Read the REAL engines requirement instead of a hardcoded "< 20":
  // engines.node is ">= 24.0.0" today and the old check kept green-lighting
  // Node 20/22 installs (missing node:sqlite, degraded memory). Fallback 20
  // preserves the old behavior for packages without an engines field.
  const enginesNode = pkg?.engines?.node;
  const requiredMajor = Number(enginesNode?.match(/\d+/)?.[0] ?? 20);
  if (major < requiredMajor) {
    return FAIL(
      `node ${raw} is older than the required engines.node (${enginesNode ?? ">= 20.0.0"})\n` +
        `         fix:  install Node.js ${requiredMajor}+ (LTS) and reopen the terminal`,
    );
  }
  return OK(`node ${raw} (engines.node ${enginesNode ?? ">= 20.0.0"})`);
}

/** Check the CLI bundle exists. */
function checkBundle(): CheckResult {
  const bundle = path.join(packageRoot, "dist", "cli", "main.bundled.js");
  if (!existsSync(bundle)) {
    return FAIL(
      `dist/cli/main.bundled.js missing at ${bundle}\n` +
        `         fix:  npm run build:cli   (then reinstall or run via tsx)`,
    );
  }
  try {
    const st = statSync(bundle);
    return OK(`bundle OK (${(st.size / 1024 / 1024).toFixed(2)} MB)`);
  } catch (err) {
    return FAIL(
      `could not stat bundle: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** Check each runtime dep is resolvable. */
function checkRuntimeDeps(): CheckResult {
  // Runtime deps are the modules the bundled CLI imports as externals (see
  // scripts/bundle-cli.mjs): react, ink, ink-text-input. zod is fully bundled
  // but kept here as an install-coherence probe. NOTE: react-dom is NOT a
  // runtime dep — Ink renders via react-reconciler, never react-dom — and it
  // ships in devDependencies, so listing it produced a false-positive FAIL on
  // every clean global install.
  const required = ["react", "ink", "ink-text-input", "zod"];
  const missing: string[] = [];
  for (const dep of required) {
    try {
      // createRequire from the package root — this is what `node bin/zelari-code.js` uses.
      const localReq = createRequire(path.join(packageRoot, "package.json"));
      localReq.resolve(dep);
    } catch {
      missing.push(dep);
    }
  }
  if (missing.length === 0) {
    return OK(
      "runtime deps resolvable (react, ink, ink-text-input, zod)",
    );
  }
  return FAIL(
    `missing runtime deps: ${missing.join(", ")}\n` +
      `         fix:  npm install -g ${"<pkg>"} --force    (then reopen the terminal)`,
  );
}

/** Check PATH includes the npm global prefix. */
function checkPath(): CheckResult {
  const prefix = getGlobalPrefix();
  if (!prefix) return WARN("npm global prefix not detectable");
  const pathSep = process.platform === "win32" ? ";" : ":";
  const pathDirs = (process.env.PATH || "").split(pathSep);
  if (pathDirs.includes(prefix)) {
    return OK(`PATH includes npm prefix (${prefix})`);
  }
  const winHint =
    `         fix:  zelari-code --fix-path   (adds ${prefix} to the user PATH)\n` +
    `               then open a NEW terminal and run \`zelari-code --version\`\n` +
    `               if it persists: see scripts/diagnose-path.ps1 in the repo`;
  const posixHint =
    `         fix:  export PATH="${prefix}/bin:$PATH"\n` +
    `               (add it to ~/.bashrc / ~/.zshrc to persist)`;
  return WARN(
    `PATH does not include npm prefix (${prefix})\n` +
      `         symptom: "zelari-code: command not found" after install\n` +
      (process.platform === "win32" ? winHint : posixHint),
  );
}

/**
 * Check that the recommended tool-budget env vars are set so the agent
 * doesn't get force-summarized mid-task by the hard cap. Reads process.env
 * (not the registry) — what the running process actually sees.
 */
function checkBudget(): CheckResult {
  const expected = {
    ZELARI_MAX_TOOL_LOOP_HARD: "180",
    ZELARI_MAX_TOOL_LOOP_ITERATIONS: "60",
    ZELARI_CONTEXT_LIMIT: "400000",
  };
  const missing: string[] = [];
  const wrong: string[] = [];
  for (const [name, target] of Object.entries(expected)) {
    const current = process.env[name];
    if (current === undefined || current === "") missing.push(name);
    else if (current !== target) wrong.push(`${name}=${current} (recommended ${target})`);
  }
  if (missing.length === 0 && wrong.length === 0) {
    return OK(`budget at recommended values (hard=180, soft=60, ctx=400k)`);
  }
  const lines: string[] = [];
  if (missing.length > 0) {
    lines.push(
      `missing: ${missing.join(", ")} — agent uses tighter in-code defaults`,
    );
  }
  if (wrong.length > 0) {
    lines.push(`override: ${wrong.join("; ")}`);
  }
  const winHint =
    `         fix:  zelari-code --fix-budget   (sets all three at User scope)\n` +
    `               then open a NEW terminal for the changes to take effect`;
  const posixHint =
    `         fix:  export ZELARI_MAX_TOOL_LOOP_HARD=180\n` +
    `               export ZELARI_MAX_TOOL_LOOP_ITERATIONS=60\n` +
    `               export ZELARI_CONTEXT_LIMIT=400000\n` +
    `               (add to ~/.bashrc or ~/.zshrc to persist)`;
  return WARN(
    `${lines.join("\n")}\n` +
      `         symptom: agent stops mid-task without completing (not rate limit)\n` +
      (process.platform === "win32" ? winHint : posixHint),
  );
}

/**
 * Adapt a `PrereqResult` (from prereqChecks.ts) into doctor's `CheckResult`.
 * The shapes are isomorphic — this is a pure pass-through that lets the
 * agent-shell-aware checks share the doctor's report formatting + summary.
 * Keep severity semantics intact: 'critical' FAILs the report, 'warn' is advisory.
 */
function prereqToCheckResult(r: PrereqResult): CheckResult {
  if (r.ok) return OK(r.message);
  return r.severity === "critical"
    ? FAIL(r.message, "critical")
    : WARN(r.message);
}

/**
 * Check the optional tool plugins (Playwright, eslint, ruff, LSP servers).
 *
 * Each plugin maps to an edge feature that degrades silently when its binary
 * is absent. doctor surfaces them as WARN (never critical — they're optional)
 * so the user learns what's missing and how to install it (`/plugins` or the
 * boot gate). Delegates to detectMissingPlugins so detection logic stays in
 * one place (plugins/registry.ts).
 */
async function checkOptionalPlugins(): Promise<CheckResult> {
  const { detectMissingPlugins } = await import("../plugins/registry.js");
  let missing;
  try {
    missing = await detectMissingPlugins(packageRoot, { includeMuted: true });
  } catch {
    return WARN("could not detect optional plugins");
  }
  if (missing.length === 0) return OK("all optional plugins available");
  const lines = missing.map((p) => `${p.id} missing — /plugins install ${p.id}`);
  return WARN(
    `${missing.length} optional plugin(s) missing (features degrade silently without them):\n` +
      lines.map((l) => `         ${l}`).join("\n") +
      "\n         install via: /plugins  (or the boot prompt)",
  );
}

/**
 * Optional Cua Driver binary (desktop computer-use via MCP).
 * WARN-only: coding works without it; agent gains native GUI control when present.
 */
function checkCuaDriver(): CheckResult {
  if (process.env["ZELARI_CUA"] === "0") {
    return OK("Cua Driver skipped (ZELARI_CUA=0)");
  }
  const ver = tryExec("cua-driver --version");
  if (!ver) {
    return WARN(
      "cua-driver not on PATH (optional desktop computer-use)\n" +
        "         install: https://cua.ai/docs/how-to-guides/driver/install\n" +
        "         then:    zelari-code --set-mcp-preset cua\n" +
        "         verify:  cua-driver doctor",
    );
  }
  return OK(`cua-driver available (${ver.split("\n")[0]})`);
}

/**
 * Run all checks and print results. Returns true if install is healthy
 * (no critical failures), false otherwise. Never throws.
 *
 * Async since v1.5.0: the optional-plugins check delegates to
 * detectMissingPlugins (dynamic import + async detection). The single caller
 * in main.ts awaits the result.
 */
/**
 * Fase M: context-growth summary over the metrics tail. Informational:
 * OK with aggregate lines when instrumented runs exist, WARN pointing at
 * the metrics file otherwise (fresh installs, or all runs pre-Fase-M).
 */
async function checkContextGrowth(): Promise<CheckResult> {
  try {
    const logger = getMetricsLogger();
    const records = await readMetrics(logger.filePath);
    const summary = summarizeContextGrowth(
      records.filter(r => r.kind === "run"),
    );
    if (!summary) {
      return WARN(
        `no instrumented runs yet (${logger.filePath}) — run a session, then re-check`,
      );
    }
    const lines = formatContextGrowthSummary(summary);
    return OK(lines.join("\n\n         "));
  } catch (err) {
    return WARN(`metrics unreadable: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * 2.31 B1: structured doctor report for the first-run gate — the SAME
 * checks as `runDoctor`, collected without printing. The wizard stops on
 * the first BLOCKING red (severity critical — its message already carries
 * the fix command: `/login …`, `--trust …`, `--fix-path`); WARN entries
 * stay in `entries` for display but never gate. This mirrors `runDoctor`'s
 * exit code (`criticalFails === 0`), so `--doctor` and `--doctor --json`
 * can never disagree; the CLI flag keeps the full human report.
 */
export interface DoctorEntry {
  name: string;
  ok: boolean;
  severity: "critical" | "warn" | "none";
  message: string;
}

export interface DoctorReport {
  entries: DoctorEntry[];
  firstRed: DoctorEntry | null;
  healthy: boolean;
}

/**
 * Gate semantics shared by every DoctorReport consumer (wizard gate in
 * main.ts, `--doctor --json` exit code, Desktop first-run gate): only a
 * critical failure blocks. A WARN-only report is healthy — informational
 * checks (context growth, optional plugins, cua-driver, budget hints)
 * must never close the front door.
 */
export function firstBlockingRed(entries: DoctorEntry[]): DoctorEntry | null {
  return entries.find((e) => !e.ok && e.severity === "critical") ?? null;
}

function buildDoctorChecks(
  pkg: ReturnType<typeof readPackageJson>,
  pkgName: string,
): Array<{ name: string; run: () => CheckResult | Promise<CheckResult> }> {
  return [
    // --- install-health checks (main-process probes) ---
    { name: "node", run: () => checkNode(pkg) },
    { name: "bin shim", run: () => checkShim(pkgName) },
    { name: "cli bundle", run: () => checkBundle() },
    { name: "runtime deps", run: () => checkRuntimeDeps() },
    { name: "PATH", run: () => checkPath() },
    // --- identity wave: can you actually build here? (key + trust) ---
    // Lazy dynamic import: doctor must stay import-light and never let a
    // config read failure crash the whole diagnostic (fail-open per check).
    // NOTE: `require()` does NOT work here — the esbuild bundle flattens the
    // module tree, so a runtime-relative require resolves against dist/ and
    // misses ("Cannot find module '../providerConfig.js'"). A literal
    // `await import()` is resolved at BUNDLE time and stays lazy at runtime.
    {
      name: "provider key",
      run: async () => {
        try {
          const { getActiveProvider } = await import("../providerConfig.js");
          const { resolveApiKey, getOAuthToken } = await import("../keyStore.js");
          const active = getActiveProvider();
          if (resolveApiKey(active.id)) {
            return OK(`provider ${active.id} — key resolved`);
          }
          if (getOAuthToken(active.id)) {
            return OK(`provider ${active.id} — OAuth token stored`);
          }
          return FAIL(
            `provider ${active.id} — no key or OAuth token. In the app: /login ${active.id}, or set ${active.envVar}`,
            "warn",
          );
        } catch (err) {
          return WARN(`provider config unreadable: ${err instanceof Error ? err.message : String(err)}`);
        }
      },
    },
    {
      name: "folder trust",
      run: async () => {
        try {
          const { isFolderTrusted, listTrustedFolders } = await import("../safety/folderTrust.js");
          const cwd = process.cwd();
          if (isFolderTrusted(cwd)) {
            return OK(`${cwd} trusted (project MCP/hooks/extensions active)`);
          }
          const n = listTrustedFolders().length;
          return WARN(
            `${cwd} NOT trusted (${n} folder(s) trusted) — project MCP/hooks/extensions stay off. Fix: zelari-code --trust "${cwd}"`,
          );
        } catch (err) {
          return WARN(`trust store unreadable: ${err instanceof Error ? err.message : String(err)}`);
        }
      },
    },
    // --- agent-shell checks (v1.4.0) ---
    // These probe node/git/bash THROUGH the resolved shell the agent uses.
    // A pass on "node" + a FAIL on "node (agent shell)" is the tell-tale
    // signature of the PATH-mismatch bug (node visible to the main process,
    // invisible to Git Bash) that silently breaks council builds.
    { name: "node (agent shell)", run: () => prereqToCheckResult(checkAgentNode()) },
    { name: "git (agent shell)", run: () => prereqToCheckResult(checkAgentGit()) },
    { name: "bash", run: () => prereqToCheckResult(checkAgentBash()) },
    // --- tool-budget sanity (v1.20.0) ---
    // WARNS when the recommended ZELARI_* caps are unset/overridden, so users
    // learn why the agent stops mid-task without completing. Points to --fix-budget.
    { name: "budget", run: () => checkBudget() },
    // --- context-growth metrics (Fase M, 2026-07 plan) ---
    // Informational aggregate over metrics.jsonl: tool round-trips,
    // intermediate tool bytes, history@request size, cache-hit tokens.
    { name: "context growth", run: () => checkContextGrowth() },
    // --- optional tool plugins (v1.5.0) ---
    // Playwright / eslint / ruff / LSP servers. WARN-only (never critical):
    // these power edge features that degrade silently when absent. Surfaced
    // here so `zelari-code --doctor` tells the user what's missing + how to
    // install (`/plugins` or the boot PluginGate).
    { name: "plugins", run: () => checkOptionalPlugins() },
    // --- optional desktop computer-use (Cua Driver, trycua) ---
    { name: "cua-driver", run: () => checkCuaDriver() },
  ];
}

export async function collectDoctorReport(): Promise<DoctorReport> {
  const pkg = readPackageJson();
  const checks = buildDoctorChecks(pkg, pkg?.name ?? "zelari-code");
  const entries: DoctorEntry[] = [];
  for (const c of checks) {
    let result: CheckResult;
    try {
      result = await c.run();
    } catch (err) {
      result = FAIL(
        `unexpected error: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    entries.push({
      name: c.name,
      ok: result.ok,
      severity: result.ok ? "none" : (result.severity ?? "warn"),
      message: result.message,
    });
  }
  const firstRed = firstBlockingRed(entries);
  return { entries, firstRed, healthy: firstRed === null };
}

export async function runDoctor(): Promise<boolean> {
  const pkg = readPackageJson();
  const pkgName = pkg?.name ?? "zelari-code";
  const checks = buildDoctorChecks(pkg, pkgName);

  // eslint-disable-next-line no-console
  console.log(`zelari-code doctor (v${pkg?.version ?? "unknown"})`);
  // eslint-disable-next-line no-console
  console.log("platform:", process.platform, process.arch);
  // eslint-disable-next-line no-console
  console.log("node:    ", process.version);
  // eslint-disable-next-line no-console
  console.log("prefix:  ", getGlobalPrefix() || "(unknown)");
  // eslint-disable-next-line no-console
  console.log("root:    ", packageRoot);
  // eslint-disable-next-line no-console
  console.log("");

  let criticalFails = 0;
  let warns = 0;

  for (const c of checks) {
    let result: CheckResult;
    try {
      result = await c.run();
    } catch (err) {
      result = FAIL(
        `unexpected error: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    const tag = result.ok
      ? "OK  "
      : result.severity === "critical"
        ? "FAIL"
        : "WARN";
    const color = result.ok
      ? "\x1b[32m"
      : result.severity === "critical"
        ? "\x1b[31m"
        : "\x1b[33m";
    const reset = "\x1b[0m";
    // eslint-disable-next-line no-console
    console.log(
      `  ${color}${tag}${reset}  ${c.name.padEnd(14)}${result.message.replace(/\n/g, "\n              ")}`,
    );
    if (!result.ok) {
      if (result.severity === "critical") criticalFails += 1;
      else warns += 1;
    }
  }

  // eslint-disable-next-line no-console
  console.log("");
  if (criticalFails === 0 && warns === 0) {
    // eslint-disable-next-line no-console
    console.log("\x1b[32m✔ all checks passed — ready to build (sei pronto a costruire)\x1b[0m");
    return true;
  }
  // eslint-disable-next-line no-console
  console.log(`✗ ${criticalFails} critical failure(s), ${warns} warning(s)`);
  // eslint-disable-next-line no-console
  console.log("  fix the FAIL/WARN rows above, then re-run: zelari-code --doctor");
  return criticalFails === 0;
}
